import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import i18next from 'i18next';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useOnValueChange } from '@hooks-clean/index';
import { SubjectMonitoringReviewTypePostDto } from '@interfaces-clean/subject-monitoring-review-type.interface';
import { useTranslation } from '@hooks/index';

const COMPONENT_NAME = 'add-review-type-dialog';

export type AddReviewTypeForm = SubjectMonitoringReviewTypePostDto;

type AddReviewTypeDialogProps = {
    existingNames: string[];
    onClose: () => void;
    onSubmit: (data: AddReviewTypeForm) => void;
    isSubmitting: boolean;
};

const AddReviewTypeDialog = ({ existingNames, onClose, onSubmit, isSubmitting }: AddReviewTypeDialogProps) => {
    const { t } = useTranslation('admin');
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const existingNamesSet = useMemo(
        () => new Set(existingNames.map((n) => n.toLocaleLowerCase().trim())),
        [existingNames],
    );

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid },
        trigger,
    } = useForm<AddReviewTypeForm>({
        defaultValues: {
            active: true,
            name: '',
            description: '',
        },
        mode: 'all',
    });

    useOnValueChange(existingNamesSet, () => {
        if (isDirty) trigger().catch((err) => window.console.error(err));
    });

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty, setComponentHasChanges]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges(`${COMPONENT_NAME}-confirmation`);
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const isFormInvalid = !isValid || !isDirty || isSubmitting;

    return (
        <DraggableDialog
            dataTestId="add-review-type-dialog"
            title={t('smReviewTypes.addDialog.title')}
            primaryBtnLabel={t('common:add')}
            secondaryBtnLabel={t('common:cancel')}
            primaryBtnDisabled={isFormInvalid}
            onSubmit={() => handleSubmit(onSubmit)()}
            onClose={onCloseHandler}
            initialWidth={559}
            initialHeight={400}
            bodyPadding="none"
        >
            <form id="add-review-type-form" className="k-w-full k-d-flex k-flex-column k-gap-2 k-px-3">
                <Controller
                    control={control}
                    name="active"
                    render={({ field: { onChange, value } }) => (
                        <CoreCheckbox
                            data-testid="add-review-type-active"
                            onChange={onChange}
                            value={value}
                            defaultChecked
                            label={t('smReviewTypes.fields.active')}
                        />
                    )}
                />
                <TextBoxController
                    control={control}
                    name="name"
                    data-testid="add-review-type-name"
                    label={`${t('smReviewTypes.fields.name')}*`}
                    maxLength={70}
                    required
                    validateRules={{
                        uniqueValue: (value) => {
                            const trimmedValue = ((value ?? '') as string).toLocaleLowerCase().trim();
                            if (existingNamesSet.has(trimmedValue)) {
                                return i18next.t('admin:smReviewTypes.validation.nameInUse') as string;
                            }
                            return true;
                        },
                        whitespace: (value) => {
                            if (value && !(value as string).trim().length)
                                return i18next.t('ra:requiredFieldError') as string;
                            return true;
                        },
                    }}
                />
                <TextAreaController
                    control={control}
                    name="description"
                    data-testid="add-review-type-description"
                    label={t('smReviewTypes.fields.description')}
                    maxLength={3000}
                    rows={8}
                    resizable="none"
                />
            </form>
        </DraggableDialog>
    );
};

export default AddReviewTypeDialog;
